import React, { useCallback, useMemo, useState } from "react";

import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import LanguageIcon from "@mui/icons-material/Language";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import { Button, Stack, Typography } from "@mui/material";
import { HumanWithGearIcon } from "assets/material-custom-icons";
import { DatePickerStyled, Dropdown } from "components/forms";
import { DealRoleGuard } from "components/guards";
import { dealFormFilters } from "constants/deal-form";
import { useFormatDateHook, useModals } from "hooks";
import {
  IApiServiceError,
  useDealActions,
  useDealFormAiDataSelect,
  useDealFormDataSelect,
  useDealFormTitleSelect,
  usePickUserPermissions
} from "store";
import { DealFormFilter, UserPermissionType } from "store/types";

import { RoleName } from "../../../constants";

interface Props {
  setFilesPickerModalOpen: (value: boolean) => void;
  openShareDialog: () => void;
  filterValue: DealFormFilter;
  setFilterValue: React.Dispatch<React.SetStateAction<DealFormFilter>>;
  updateFormRecurring: (date: string) => Promise<void>;
}

export const Header: React.FC<Props> = ({
  setFilesPickerModalOpen,
  openShareDialog,
  filterValue,
  setFilterValue,
  updateFormRecurring
}) => {
  const { errorModal } = useModals();
  const { getDealFormAndStoreAction, aiAnswerFormAction } = useDealActions();
  const formatDate = useFormatDateHook();
  const formTitle = useDealFormTitleSelect();

  // const dealFormDealId = useDealFormDealIdSelect();
  const formAiData = useDealFormAiDataSelect();
  const form = useDealFormDataSelect();

  const { kind, recurring_date, id: formId, deal_id: dealId } = form || {};

  const myDeals = usePickUserPermissions("deals");
  const hideForNonAdmin =
    !!myDeals && (myDeals === UserPermissionType.NO_ACCESS || myDeals === UserPermissionType.VIEWER);

  const onFilterChange = useCallback(
    (filter: DealFormFilter) => {
      if (!formId) return;
      let prevFilter: DealFormFilter | null = null;
      try {
        setFilterValue((prevState) => {
          prevFilter = prevState;
          return filter;
        });
        getDealFormAndStoreAction({ dealId, formId, filter: filter.name });
      } catch (_error) {
        errorModal({ msg: "Failed to filter form" });
        // Revert to previous filter
        if (prevFilter) {
          setFilterValue(prevFilter);
        }
      }
    },
    [dealId, formId]
  );

  // setShareDialog({
  //   elements: [form.id],
  //   type: "form_id",
  //   shareName: form.title,
  //   dealId: form.deal_id,
  //   dealCapitalSeekerEmail: capitalSeeker?.email,
  //   itemToShare: form.is_private ? null : form
  // });

  const [anchorEl, setAnchorEl] = useState(null);
  const handleOpen = (event: any) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const onDatePickerSave = useCallback(
    async (date: string) => {
      try {
        if (date.toString() === recurring_date) {
          return;
        }
        await updateFormRecurring(date);
        handleClose();
      } catch (error) {
        errorModal({ msg: (error as IApiServiceError)?.errors?.recurring_date?.[0] });
        throw error;
      }
    },
    [recurring_date]
  );

  const onSearchWeb = useCallback(async () => {
    if (!formId) return;
    await aiAnswerFormAction({
      ...(dealId && { dealId }),
      formId,
      file_ids: [],
      web_search: 1
    });
    await getDealFormAndStoreAction({ dealId, formId, disableLoading: true });
  }, [dealId, formId]);

  const isAiInProgress = useMemo(() => formAiData.ai_status === "in-progress", [formAiData.ai_status]);

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        bgcolor: "#dbe8f5",
        px: "15px",
        height: "41px",
        minHeight: "41px",
        flexShrink: 0,
        boxShadow: "0px 1px 4px 0px rgba(18,18,23,0.12), 0px 1px 2px 0px rgba(18,18,23,0.08)",
        borderRadius: "8px 8px 0 0"
      }}
    >
      <Typography noWrap sx={{ fontSize: "16px", fontWeight: 500, color: "#0a3142", maxWidth: "40%" }}>
        {formTitle}
      </Typography>
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          gap: "10px",
          "@media (max-width: 1000px)": { gap: "8px" },
          "@media (max-width: 850px)": { gap: "6px" },
          "@media (max-width: 700px)": { gap: "4px" }
        }}
      >
        {kind === "recurring" && recurring_date && (
          <DealRoleGuard
            availableRoles={[RoleName.Admin, RoleName.Analyst]}
            RenderInstead={
              <Typography noWrap sx={{ fontSize: "18px", whiteSpace: "nowrap" }}>
                ({formatDate({ date: recurring_date, format: "MMM DD YYYY" })})
              </Typography>
            }
          >
            <Stack sx={{ flexDirection: "row", alignItems: "center" }}>
              <Stack
                sx={{ cursor: "pointer", flexDirection: "row", gap: "5px", alignItems: "center" }}
                onClick={handleOpen}
              >
                <Typography noWrap sx={{ fontSize: "18px", whiteSpace: "nowrap" }}>
                  {formatDate({ date: recurring_date, format: "MMM DD YYYY" })}
                </Typography>
                <KeyboardArrowDownOutlinedIcon />
              </Stack>
              <DatePickerStyled
                initialValue={recurring_date}
                onCancelClick={handleClose}
                onSave={onDatePickerSave}
                anchorEl={anchorEl!}
              />
            </Stack>
          </DealRoleGuard>
        )}

        <DealRoleGuard availableRoles={[RoleName.Admin, RoleName.Analyst]} hideChild={hideForNonAdmin}>
          <Button
            disabled={isAiInProgress}
            variant="outlined"
            sx={{
              p: "4px 24px",
              color: "#071F35",
              backgroundColor: "#fff",
              height: "24px !important",
              minHeight: "24px !important",
              "&.Mui-disabled": {
                border: "1px solid #bac7ce",
                backgroundColor: "#fff",
                color: "#bac7ce"
              }
            }}
            startIcon={<LanguageIcon sx={{ fill: isAiInProgress ? "#bac7ce" : "#071F35" }} />}
            onClick={onSearchWeb}
          >
            Search Web
          </Button>
          <Button
            disabled={isAiInProgress}
            variant="outlined"
            sx={{
              padding: "0px 8px",
              height: "24px",
              minHeight: "24px",
              color: "#071F35",
              backgroundColor: "#fff",
              "&.Mui-disabled": {
                border: "1px solid #bac7ce",
                backgroundColor: "#fff",
                color: "#bac7ce"
              }
            }}
            startIcon={<HumanWithGearIcon sx={{ fill: isAiInProgress ? "#bac7ce" : "#071F35" }} />}
            onClick={() => setFilesPickerModalOpen(true)}
          >
            Ask AI
          </Button>
        </DealRoleGuard>
        <Dropdown
          style={{ maxWidth: "150px" }}
          theme="blue-light"
          onChange={onFilterChange}
          value={filterValue}
          options={dealFormFilters}
        />
        {!!dealId && (
          <DealRoleGuard availableRoles={[RoleName.Admin, RoleName.Analyst]} hideChild={hideForNonAdmin}>
            <Button
              onClick={openShareDialog}
              variant="outlined"
              sx={{
                padding: "0px 8px",
                minWidth: "85px",
                maxWidth: "85px",
                height: "24px",
                minHeight: "24px",
                backgroundColor: "#fff"
              }}
              startIcon={<PersonAddAltIcon sx={{ color: "#0a3142" }} />}
            >
              Share
            </Button>
          </DealRoleGuard>
        )}
      </Stack>
    </Stack>
  );
};
